import { UserManager, WebStorageStateStore } from 'oidc-client';
import { recordSessionEvent } from './sessionLog';

const DEFAULT_SCOPE = 'openid profile email';

let userManager;

function getRuntimeAuthConfig() {
  return window.__DATASCOPE_CONFIG__?.auth ?? {};
}

function isRuntimeOidcExplicitlyDisabled() {
  return getRuntimeAuthConfig().oidc?.enabled === false;
}

export function getAuthMode() {
  return (getRuntimeAuthConfig().mode || process.env.REACT_APP_AUTH_MODE || 'LocalJwt').trim();
}

export function isOidcEnabled() {
  if (isRuntimeOidcExplicitlyDisabled()) return false;
  const mode = getAuthMode().toLowerCase();
  return mode === 'externaloidc' || mode === 'hybrid';
}

export function isLocalLoginEnabled() {
  const mode = getAuthMode().toLowerCase();
  return mode === 'localjwt' || mode === 'hybrid';
}

function getOidcConfig() {
  const runtimeConfig = getRuntimeAuthConfig().oidc ?? {};
  const audience = runtimeConfig.audience || process.env.REACT_APP_OIDC_AUDIENCE || '';
  return {
    authority: runtimeConfig.authority || process.env.REACT_APP_OIDC_AUTHORITY || '',
    client_id: runtimeConfig.clientId || process.env.REACT_APP_OIDC_CLIENT_ID || '',
    response_type: runtimeConfig.responseType || process.env.REACT_APP_OIDC_RESPONSE_TYPE || 'code',
    scope: runtimeConfig.scope || process.env.REACT_APP_OIDC_SCOPE || DEFAULT_SCOPE,
    redirect_uri: runtimeConfig.redirectUri || `${window.location.origin}/signin-oidc`,
    post_logout_redirect_uri: runtimeConfig.postLogoutRedirectUri || `${window.location.origin}/`,
    automaticSilentRenew: runtimeConfig.automaticSilentRenew ?? true,
    loadUserInfo: runtimeConfig.loadUserInfo ?? true,
    extraQueryParams: audience ? { audience } : {},
    userStore: new WebStorageStateStore({ store: window.localStorage }),
  };
}

export function getUserManager() {
  if (userManager) return userManager;

  const config = getOidcConfig();
  if (!config.authority || !config.client_id) {
    throw new Error('OIDC settings are missing. Authority and ClientId are required.');
  }

  userManager = new UserManager(config);
  return userManager;
}

export async function startOidcLogin() {
  await getUserManager().signinRedirect();
}

export async function completeOidcLogin() {
  const user = await getUserManager().signinRedirectCallback();
  const token = user?.access_token;
  if (!token) throw new Error('OIDC access token was not returned.');

  localStorage.setItem('token', token);
  localStorage.setItem('user', JSON.stringify({
    isLoginOk: true,
    token,
    expiration: user.expires_at ? new Date(user.expires_at * 1000).toISOString() : null,
    userName: user.profile?.name ?? user.profile?.preferred_username ?? user.profile?.email ?? user.profile?.sub,
    userEMail: user.profile?.email ?? user.profile?.preferred_username,
    tenantId: user.profile?.TenantId ?? user.profile?.tid ?? user.profile?.tenant_id,
    tenantName: user.profile?.TenantName ?? user.profile?.tid ?? user.profile?.tenant_id,
    authProvider: 'oidc',
  }));
  window.dispatchEvent(new Event('auth:login'));

  return user;
}

export async function logoutOidc() {
  // Bu yol utils/session'ı kullanmaz (sağlayıcıya yönlendirme ile biter) — günlüğe
  // kendisi yazsın ki teşhiste "sebep bilinmiyor" boşluğu kalmasın.
  recordSessionEvent('oidc-logout');
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  window.dispatchEvent(new Event('auth:logout'));

  if (!isOidcEnabled()) return;

  const manager = getUserManager();
  const user = await manager.getUser();
  if (user) await manager.signoutRedirect();
}
